import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const rows = [
    ['Jan', '$842',   true],
    ['Feb', '$1,024', true],
    ['Mar', '$1,380', true],
    ['Apr', '$2,115', true],
    ['May', '$2,840', true],
    ['Jun', '$3,420', false],
];

export default function IlluPayments() {
    const svgRef = useRef(null);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;

        const ctx = gsap.context(() => {
            const leftCard    = svg.querySelector('#py-left');
            const balanceCard = svg.querySelector('#py-balance');
            const nextCard    = svg.querySelector('#py-next');
            const rowEls      = svg.querySelectorAll('.py-row');
            const pendingIcon = svg.querySelector('#py-pending');
            const pill        = svg.querySelector('#py-pill');
            const circles     = svg.querySelector('#py-circles');

            // ── initial states ──
            gsap.set(leftCard,    { opacity: 0, x: -22 });
            gsap.set(balanceCard, { opacity: 0, scale: 0.88, svgOrigin: '343 77' });
            gsap.set(nextCard,    { opacity: 0, y: 18 });
            gsap.set(rowEls,      { opacity: 0, x: -10 });
            gsap.set(pill,        { opacity: 0, y: 6 });
            gsap.set(circles,     { opacity: 0 });

            // ── entry timeline ──
            const tl = gsap.timeline({
                scrollTrigger: { trigger: svg, start: 'top 85%', once: true },
                onComplete: startLoops,
            });

            tl
                .to(leftCard,    { opacity: 1, x: 0, duration: 0.5, ease: 'power2.out' })
                .to(rowEls,      { opacity: 1, x: 0, duration: 0.4, ease: 'expo.out', stagger: 0.08 }, '-=0.2')
                .to(balanceCard, { opacity: 1, scale: 1, duration: 0.55, ease: 'back.out(2)' }, 0.2)
                .to(circles,     { opacity: 1, duration: 0.5, ease: 'power1.out' }, 0.65)
                .to(nextCard,    { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.4)
                .to(pill,        { opacity: 1, y: 0, duration: 0.35, ease: 'back.out(2)' }, '-=0.1');

            function startLoops() {
                // Balance card floats up gently
                gsap.to(balanceCard, {
                    y: -3, duration: 2.3, ease: 'sine.inOut', yoyo: true, repeat: -1,
                });

                // Next payout card counter-floats (opposite phase)
                gsap.to(nextCard, {
                    y: 3, duration: 2.7, ease: 'sine.inOut', yoyo: true, repeat: -1,
                });

                // Pending "…" blinks to signal in-progress
                gsap.to(pendingIcon, {
                    opacity: 0.2, duration: 0.9, ease: 'sine.inOut',
                    yoyo: true, repeat: -1, repeatDelay: 0.5,
                });

                // Min $50 pill gentle scale pulse
                gsap.to(pill, {
                    scale: 1.06, svgOrigin: '330 232',
                    duration: 1.9, ease: 'sine.inOut', yoyo: true, repeat: -1,
                });

                // Decorative circles slow opacity breathe
                gsap.to(circles, {
                    opacity: 0.55, duration: 2.8, ease: 'sine.inOut', yoyo: true, repeat: -1,
                });
            }
        }, svgRef);

        return () => ctx.revert();
    }, []);

    return (
        <svg ref={svgRef} viewBox="0 0 460 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="pxl-al-illu-svg">
            <defs>
                <linearGradient id="py-wallet" x1="0" y1="0" x2="0" y2="1">
                    <stop stopColor="#7c3aed"/><stop offset="1" stopColor="#5b21b6"/>
                </linearGradient>
                <linearGradient id="py-bg" x1="0" y1="0" x2="1" y2="1">
                    <stop stopColor="#f0eaff"/><stop offset="1" stopColor="#e6deff"/>
                </linearGradient>
                <filter id="py-shadow" x="-5%" y="-10%" width="110%" height="130%">
                    <feDropShadow dx="0" dy="6" stdDeviation="12" floodColor="#4c1d95" floodOpacity="0.10"/>
                </filter>
                <filter id="py-sm" x="-8%" y="-12%" width="116%" height="140%">
                    <feDropShadow dx="0" dy="3" stdDeviation="7" floodColor="#000" floodOpacity="0.09"/>
                </filter>
            </defs>

            <rect width="460" height="280" fill="url(#py-bg)"/>

            {/* Left panel – payout history */}
            <g id="py-left">
                <rect x="18" y="14" width="212" height="252" rx="18" fill="#fff" filter="url(#py-shadow)"/>
                <rect x="18" y="14" width="212" height="52" rx="18" fill="#7c3aed"/>
                <rect x="18" y="44" width="212" height="22" fill="#7c3aed"/>
                <text x="124" y="44" textAnchor="middle" fontSize="12.5" fill="#fff" fontWeight="700" fontFamily="inherit">Payout History</text>

                {rows.map(([month, amount, paid], i) => (
                    <g key={month} className="py-row">
                        <line x1="34" y1={80 + i*30} x2="214" y2={80 + i*30} stroke="#f8f5ff" strokeWidth="1"/>
                        <text x="46"  y={97 + i*30} fontSize="11" fill="#374151" fontFamily="inherit">{month}</text>
                        <text x="190" y={97 + i*30} fontSize="11" fontWeight="700"
                            fill={paid ? '#059669' : '#d97706'} textAnchor="end" fontFamily="inherit">{amount}</text>
                        <circle cx="206" cy={92 + i*30} r="7" fill={paid ? '#dcfce7' : '#fef3c7'}/>
                        {paid
                            ? <path d={`M${202},${92+i*30} l3,3 l5,-5`}
                                stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            : <g id="py-pending">
                                <text x="206" y={96+i*30} textAnchor="middle" fontSize="9" fill="#d97706" fontFamily="inherit">…</text>
                              </g>
                        }
                    </g>
                ))}
            </g>

            {/* Right top – balance card */}
            <g id="py-balance">
                <rect x="244" y="14" width="198" height="126" rx="18" fill="url(#py-wallet)" filter="url(#py-sm)"/>
                <text x="262" y="44" fontSize="10.5" fill="#ddd6fe" fontFamily="inherit">Current Balance</text>
                <text x="262" y="80" fontSize="30" fontWeight="800" fill="#fff" fontFamily="inherit">$3,420</text>
                <line x1="262" y1="98" x2="424" y2="98" stroke="#fff" strokeWidth="1" opacity="0.2"/>
                <text x="262" y="118" fontSize="10" fill="#c4b5fd" fontFamily="inherit">via PayPal · payout Jun 1</text>
                <g id="py-circles">
                    <circle cx="398" cy="78" r="16" fill="#fff" opacity="0.08"/>
                    <circle cx="414" cy="78" r="16" fill="#fff" opacity="0.06"/>
                </g>
            </g>

            {/* Right bottom – next payout */}
            <g id="py-next">
                <rect x="244" y="152" width="198" height="114" rx="18" fill="#fff" filter="url(#py-sm)"/>
                <text x="262" y="180" fontSize="10.5" fill="#9ca3af" fontFamily="inherit">Next payout</text>
                <text x="262" y="208" fontSize="17" fontWeight="800" fill="#1a1535" fontFamily="inherit">June 1, 2025</text>
                <g id="py-pill">
                    <rect x="262" y="220" width="136" height="24" rx="12" fill="#f5f0ff"/>
                    <text x="330" y="236" textAnchor="middle" fontSize="9.5" fill="#7c3aed" fontWeight="600" fontFamily="inherit">Min $50 · auto-transfer</text>
                </g>
            </g>
        </svg>
    );
}
